'use client'

import * as React from 'react'

import {
  ArrowRightIcon,
  ChevronDownIcon,
  CopyIcon,
  PlayIcon,
} from '@radix-ui/react-icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { Label } from '~/sotano-kit/primitives/input'
import { usePreviewLink } from '~/hooks-data/preview-link'
import { Block, isValidBlockPreviewType } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { useTabsStore } from '../store'
import { useSearchParams } from 'next/navigation'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import {
  useIsExternalVisitor,
  useIsMemberOrHigher,
} from '~/hooks-data/use-user-role'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import {
  GetVariables,
  VariablesInput,
} from '~/components/variables/single-line-input'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { TreeManager } from '@basehub/utils/tree/index'
import { cva } from 'class-variance-authority'
import Link from 'next/link'

export const $breadcrumbCta = cva(
  'text-dark-muted flex items-center justify-center -outline-offset-1 gap-1.5 rounded outline-1 outline-dark-control-base focus-visible:outline',
  {
    variants: {
      pointer: {
        true: 'enabled:cursor-pointer hover:underline',
        false: 'cursor-default',
      },
      disabled: {
        false: 'hover:text-dark-bright',
        true: 'cursor-default opacity-40 hover:no-underline',
      },
    },
    defaultVariants: {
      disabled: false,
      pointer: false,
    },
  }
)

export const PreviewButton = React.forwardRef<
  HTMLDivElement,
  {
    blockId: string
    readOnly?: boolean
    treeManager: TreeManager<Block, NonNullable<unknown>>
  }
>(({ blockId, readOnly, treeManager }, previewRef) => {
  const isExternalVisitor = useIsExternalVisitor()
  const { getVariables, previewLink, templateBlock, value, block, onChange } =
    usePreviewLink({ blockId, treeManager })

  const optionsButtonRef = React.useRef<HTMLButtonElement>(null)

  return (
    <div className="flex items-stretch shrink-0" ref={previewRef}>
      <Link
        rel="noopener noreferrer"
        target="_blank"
        prefetch={false}
        className={$breadcrumbCta({ disabled: !previewLink, pointer: true })}
        href={previewLink}
        onClick={(e) => {
          if (!previewLink) {
            e.preventDefault()
            optionsButtonRef.current?.click()
          }
        }}
      >
        <PlayIcon className="w-3 shrink-0" />
        <span className="font-medium">Preview</span>
      </Link>
      {!isExternalVisitor && !readOnly && block && (
        <PreviewPopover
          key={blockId}
          onChange={onChange}
          value={value}
          previewLink={previewLink}
          block={block}
          getVariables={getVariables}
          templateBlock={templateBlock}
        >
          <Button
            unstyled
            focusStyled
            className="text-dark-muted p-1 flex items-center justify-center hover:text-dark-bright cursor-default !-outline-offset-1 aspect-square"
            ref={optionsButtonRef}
          >
            <ChevronDownIcon className="size-3.5" />
          </Button>
        </PreviewPopover>
      )}
    </div>
  )
})

const PreviewPopover = ({
  onChange,
  value,
  block,
  children,
  templateBlock,
  previewLink,
  getVariables,
}: {
  onChange: (value: string) => void
  value: string
  previewLink: string
  block: Block
  templateBlock?: Block
  children: React.ReactNode
  getVariables: GetVariables
}) => {
  const isMemberOrOwner = useIsMemberOrHigher()
  const isDisabled =
    !!templateBlock || !isMemberOrOwner || !isValidBlockPreviewType(block)
  const addTab = useTabsStore((state) => state.addTab)
  const { isOn, handleOn, handleOff } = useToggleState()
  const searchParams = useSearchParams()
  const isPreviewPopover = React.useMemo(
    () => searchParams.get('preview-popover') === 'true',
    [searchParams]
  )

  React.useEffect(() => {
    if (isPreviewPopover) {
      handleOn()
    }
  }, [isPreviewPopover, handleOn])

  return (
    <Popover
      open={isOn}
      onOpenChange={(open) => {
        if (!open) {
          window.history.pushState({}, '', window.location.pathname)
          handleOff()
          return
        }
        window.history.pushState({}, '', '?preview-popover=true')
        handleOn()
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        asChild
        side="bottom"
        align="end"
        alignOffset={4}
        sideOffset={6}
        className="!p-0 !overflow-visible"
      >
        <div className="flex flex-col !px-0 !pt-0 pb-3 w-[400px] relative p-4">
          <div className="px-4 py-3 relative flex flex-col space-y-1">
            {isDisabled ? (
              <>
                <span className="text-dark-dim text-xs font-medium">
                  Preview URL
                </span>
                {previewLink ? (
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={previewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-dark-muted hover:text-dark-dim underline whitespace-nowrap inline-flex items-center text-xs"
                  >
                    <span className="truncate flex-1 pr-2">{previewLink}</span>
                  </ButtonLink>
                ) : (
                  <span className="text-dark-muted italic text-xs">
                    No preview URL
                  </span>
                )}
                {templateBlock && (
                  <Button
                    unstyled
                    focusStyled
                    onClick={() => {
                      addTab({ key: templateBlock.id, partiallyOpen: true })
                      window.history.pushState({}, '', '?preview-popover=true')
                    }}
                    className="inline-flex items-center gap-2 pt-0.5 font-medium group text-xs text-dark-bright cursor-pointer max-w-max"
                  >
                    <span className="group-hover:underline">
                      Edit on main component
                    </span>
                    <ArrowRightIcon className="size-3 group-hover:translate-x-1 transition-transform" />
                  </Button>
                )}
              </>
            ) : (
              <>
                <Label
                  label="Preview URL"
                  size="xs"
                  className="!m-0"
                  blocked={false}
                  onClick={(e) => {
                    e.currentTarget
                      .querySelector<HTMLDivElement>('[contenteditable="true"]')
                      ?.focus()
                  }}
                >
                  <VariablesInput
                    value={value}
                    setValue={(v) => onChange(v)}
                    getVariables={(...args) => {
                      const vars = getVariables(...args)
                      return vars
                    }}
                    autoFocus
                    variant="input"
                    onSubmit={() => {
                      window.history.pushState({}, '', window.location.pathname)
                      handleOff()
                    }}
                  />
                </Label>
                <span
                  className={clsx(
                    'text-dark-muted text-xs min-h-[17px] break-words w-full',
                    'min-h-[24px]'
                  )}
                >
                  {previewLink ? (
                    <span className="inline-flex items-center overflow-hidden text-ellipsis whitespace-nowrap w-full">
                      <a
                        href={previewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-words hover:text-dark-dim underline whitespace-nowrap overflow-hidden text-ellipsis inline-flex items-center w-full"
                      >
                        <span className="truncate flex-1 pr-2">
                          {previewLink}
                        </span>
                      </a>
                      <CopyTooltip
                        copyContent={previewLink}
                        label={
                          <IconButton className="shrink-0" asChild>
                            <span>
                              <CopyIcon />
                            </span>
                          </IconButton>
                        }
                      />
                    </span>
                  ) : (
                    <span className="italic">No preview URL</span>
                  )}
                  {!value && previewLink && (
                    <>
                      <br />
                      <span className="text-dark-faint italic">
                        {' '}
                        (using parent URL)
                      </span>
                    </>
                  )}
                </span>
              </>
            )}
          </div>
          <div className="sticky left-0 bottom-0 bg-dark-shade px-4 py-3 flex justify-between rounded-b-[7px] border-t border-dark-border-solid">
            <span className="text-dark-faint text-xs font-medium">
              {isDisabled
                ? 'Preview URL inherited from template.'
                : 'Press Ctrl+Space to add variables.'}
            </span>
            <ButtonLink
              href="/help/dashboard/preview"
              target="_blank"
              rel="noopener noreferrer"
              unstyled
              className="text-dark-bright text-xs hover:underline font-medium"
            >
              Docs
            </ButtonLink>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
